import { createContext, useContext, useEffect, useState } from "react";

const translations = {
    en: {
        nav: {
            overview: "Overview",
            video: "Video",
            gallery: "Gallery",
            readPaper: "Read paper",
        },
        hero: {
            tagYear: "Research Project",
            tagDomain: "Robotics · AI · SAR",
            titleLine1: "Search and Rescue",
            titleLine2: "Robot",
            tagline:
                "Autonomous IR human detection & mapping for the critical first hours of disaster response.",
            body: "An autonomous robot that fuses RGB and infrared vision with a hybrid SLAM framework to map unknown environments, turning frantic search into precise, life-saving extraction.",
            ctaMission: "See the mission",
            ctaMethodology: "View methodology",
            ctaVideo: "Watch video",
            missionTime: "Mission Time",
            missionStatus: "Survivor located in degraded vision conditions.",
            unitLabel: "UNIT · D-07",
            statusLabel: "STATUS · ACTIVE",
            feedLabel: "RGB + IR · FUSION",
            recLabel: "REC",
            pillLabel: "Status",
            pillBody: "Survivor detected in degraded vision conditions.",
        },
        marquee: [
            "ArUcoSLAM",
            "Infrared Vision",
            "Deep Learning Detection",
            "Wheel Odometry Fusion",
            "Semantic Mapping",
            "GPS-Denied Environments",
            "Autonomous Exploration",
        ],
        journey: {
            tag: "The Mission · Story",
            heading: "From rubble to rescue, in five moves.",
            narrative:
                "After an earthquake, the single most critical piece of information for search-and-rescue teams is the exact location of the people trapped under the rubble. We designed a system that detects them and reports their coordinates directly to rescue teams. The full algorithmic stack was first validated in simulation, then deployed on a real robot. Because environments under rubble are dark, we added an IR camera and trained a fused RGB + IR human-detection model, where standard RGB collapses in low light, our model keeps finding the survivor. And because GPS does not work indoors or under debris, the robot self-localizes using wheel odometry and ArUco markers. The result is a robot that maps the space, detects humans in low light, and continuously updates its own pose in real time.",
            steps: [
                {
                    title: "Trapped under rubble",
                    body: "After an earthquake the bottleneck for rescue teams is not strength, it is information. Where, exactly, are the survivors?",
                },
                {
                    title: "A coordinate, not a search",
                    body: "We designed an algorithmic stack that detects people under collapsed structures and relays exact coordinates to rescue teams.",
                },
                {
                    title: "Tested twice",
                    body: "The full pipeline was validated first in simulation, then re-validated on a real robot in our lab.",
                },
                {
                    title: "Seeing in the dark",
                    body: "Under rubble it is dark. We mounted an IR camera, trained a human-detection model and fused RGB + IR, where ordinary RGB fails, our model still finds the survivor.",
                },
                {
                    title: "Mapping without GPS",
                    body: "GPS does not work under collapsed structures. Wheel odometry + ArUco markers let the robot self-localize, map the space and continuously update its pose in real time.",
                },
            ],
        },
        intro: {
            tag: "01 · Introduction",
            heading: "The first hours are everything.",
            body: "In disaster response, the first hours are critical. Our search-and-rescue robot maps unknown environments and provides real-time survivor coordinates, enabling rescue teams to bypass the search phase and move directly to life-saving extraction.",
            cards: [
                {
                    label: "01",
                    title: "Multimodal Perception",
                    body: "RGB and infrared vision fuse to detect victims in total darkness, smoke-filled rooms and visually degraded zones, without losing detection confidence.",
                },
                {
                    label: "02",
                    title: "Precision Localization",
                    body: "ArUcoSLAM combined with wheel odometry delivers high-accuracy positioning, navigating complex obstacles where GPS fails entirely.",
                },
                {
                    label: "03",
                    title: "Tactical Semantic Mapping",
                    body: "Real-time detection and depth estimation pinpoint survivor coordinates and anchor them as unique landmarks on a live 2D dashboard.",
                },
                {
                    label: "04",
                    title: "Operational Efficiency",
                    body: "Pre-verified survivor locations eliminate the manual search phase, letting rescue teams move directly to life-saving extraction.",
                },
            ],
        },
        research: {
            tagQ: "02 · Research Question",
            question:
                "How effectively can a hybrid localization system maintain mapping accuracy for survivor detection in disastrous environments?",
            tagObj: "Research Objectives",
            objectives: [
                "Develop a stable localization framework using ArUcoSLAM and wheel encoders that functions reliably in GPS-denied, visually degraded disaster zones.",
                "Evaluate the effectiveness of integrating RGB and infrared (IR) data for identifying survivors in disastrous conditions.",
                "Implement a dynamic spatial map that automatically pinpoints survivor locations and shares actionable coordinates with rescue teams.",
                "Minimize the “search-to-rescue” transition time by providing a verified map of the disaster environment.",
            ],
        },
        methodology: {
            tag: "03 · Methodology",
            heading: "Four pillars of an autonomous mission.",
            sub: "Each module is engineered to operate independently and converge into a single coherent loop, perception, localization, mapping, exploration.",
            cards: [
                {
                    title: "Hybrid Localization",
                    body: "Precise positioning using ArUco markers and wheel odometry for continuous, drift-free autonomous navigation.",
                },
                {
                    title: "Deep Learning Detection",
                    body: "Real-time human identification model fine-tuned for low-light, low-contrast disaster scenarios.",
                },
                {
                    title: "Semantic Mapping",
                    body: "Instant coordinate calculation, depth projection and marking of survivors on a live 2D dashboard.",
                },
                {
                    title: "Autonomous Exploration",
                    body: "Self-governing search-and-rescue mission planning in fully unknown environments.",
                },
            ],
        },
        positioning: {
            tag: "04, Positioning Reliability",
            heading: "A fused pose that never loses the room.",
            hybridTitle: "Hybrid Fusion",
            hybridBody:
                "ArUco markers combined with wheel odometry eliminated positioning drift across long autonomous traversals.",
            stabilityTitle: "Stability",
            stabilityBody:
                "The system maintained a consistent fused pose even when visual markers were temporarily obscured or lighting was insufficient.",
            stats: [
                { v: "≈0 cm", l: "Drift / 10 m" },
                { v: "RGB+IR", l: "Vision Mode" },
                { v: "Live", l: "Mapping" },
            ],
            imgTop: "RGB · IR · DUAL FEED",
            imgBottom: "SURVIVOR DOWN",
            imgAlt: "RGB vs IR vision side-by-side comparison on the SAR test track",
        },
        chart: {
            tag: "Mission · Infographic",
            heading: "The road from disaster to rescue.",
            sub: "A single visual that follows the mission end-to-end, each waypoint represents a stage of the autonomous search-and-rescue pipeline.",
            figLabel: "Fig.02 · Mission Path",
            figRight: "SAR · 2025",
            imgAlt: "Mission journey infographic, five waypoints from disaster strike to rescue and extraction.",
            legend: [
                { n: "01", l: "Disaster strikes" },
                { n: "02", l: "Site located" },
                { n: "03", l: "Robot deployed" },
                { n: "04", l: "Survivor detected" },
                { n: "05", l: "Rescue & extraction" },
            ],
        },
        analysis: {
            tag: "05 · Analysis & Discussion",
            heading: "Where RGB falls short, IR picks up the slack.",
            col1Title: "Detection Performance",
            col1: [
                {
                    t: "RGB · IR Synergy",
                    b: "While RGB vision struggled in degraded conditions, IR integration maintained high detection confidence, closing the perception gap end-to-end.",
                },
                {
                    t: "Robustness",
                    b: "The AI model successfully distinguished human targets from debris in simulated disaster conditions.",
                },
            ],
            col2Title: "Tactical Intelligence",
            col2: [
                {
                    t: "Spatial Pinpointing",
                    b: "Victim coordinates are projected onto the 2D map through real-time angle and depth estimation.",
                },
                {
                    t: "Unique Landmark Anchoring",
                    b: "The system anchors detected positions to prevent redundant markings, ensuring a clean and unique survivor list for rescue teams.",
                },
            ],
        },
        results: {
            tag: "06 · Results",
            body1: "By automating survivor localization, our system minimizes response times and",
            highlight: "bridges the gap",
            body2: "between discovery and rescue in critical disaster scenarios.",
            cta: "See gallery",
        },
        video: {
            tag: "Demonstration · 01",
            heading: "The robot in motion.",
            sub: "An autonomous traversal through a simulated disaster zone, detecting, mapping and reporting.",
            duration: "Duration",
            modules: "Modules",
            figLeft: "Fig.02 · Field traversal",
            figRight: "Field demonstration",
            abstractTag: "Abstract",
            abstractTitle:
                "From silent room to verified rescue location, in under five minutes.",
            abstractP1:
                "This demonstration captures the robot exploring a simulated, GPS-denied disaster environment. It uses ArUco markers and wheel odometry to maintain a fused pose, while RGB + infrared streams collaborate to identify a human silhouette obscured by debris and low light.",
            abstractP2:
                "Once a survivor is detected, the system projects the location onto a live 2D map and anchors it as a unique landmark, generating an actionable coordinate that can be relayed to rescue teams.",
            ctaGallery: "Open gallery",
            ctaHome: "Back to overview",
        },
        gallery: {
            tag: "Archive · Visual record",
            heading: "The project, in pictures.",
            sub: "Hardware, field scenarios, IR captures and live mapping, a curated archive of the robot in action. Click any image to enlarge.",
            captions: [
                "YOLO output, survivors detected in cluttered disaster zone (0.30 m & 0.21 m readouts)",
                "Live 2D SLAM map, robot (yellow) with vision cone and detected survivor H0",
                "Live person detection on the SAR test track, two subjects at 0.26 m (0.89) and 0.49 m (0.82)",
                "The robot in the field, dual cameras and a 3D-printed survivor figurine for scale",
                "RGB vs IR vision side-by-side, standing person and fallen survivor on the test track",
                "The chassis, Raspberry Pi 4, Pi camera, dual-vision rig and yellow wheels",
                "Top-down view of the simulated indoor disaster environment with multiple survivors",
                "Disaster simulation, alternate state with the robot entering the structure",
                "RGB & IR dual pipeline, same scene rendered in two perception channels",
                "Handheld ArUco marker (id=0) with live pose axes overlay, single-marker localization",
                "ArUco mode, real-time marker localization in the lab via rqt image view",
                "Marker ID:0 selected on the ArUcoSLAM grid (0–15), pose and coordinates locked",
                "YOLO inference, bounding boxes with depth & confidence on the SAR test track",
                "duckie_processed vs pi_camera, RGB feed alongside the processed IR pipeline output",
            ],
        },
        footer: {
            tagline:
                "Bridging the gap between discovery and rescue, one autonomous mission at a time.",
            sections: "Sections",
            overview: "Project overview",
            video: "Demonstration video",
            gallery: "Image gallery",
            focus: "Research focus",
            focusList: [
                "Autonomous Search & Rescue",
                "Hybrid RGB + Infrared Vision",
                "ArUcoSLAM Localization",
                "Real-time Semantic Mapping",
            ],
            copyright: "Search and Rescue Robot Research Project. All rights reserved.",
            tagsBottom: "Autonomous · IR · Detection · Mapping",
            supervisorLabel: "Supervisor",
            supervisorName: "Dr. Öğr. Üyesi Özgür Erkent",
            membersLabel: "Members",
            members: ["Sümeyra Koç", "Mert Kılınçer", "Ayşe Zülal Yıldız"],
            ackLabel: "Acknowledgement",
            ackText: "The Duckiebots were provided by the project \"Teaching SLAM with Autonomous Robots For Rescue Tasks\" funded by Bridge to Turkey Fund with sponsorship from NVIDIA.",
        },
    },
    tr: {
        nav: {
            overview: "Genel Bakış",
            video: "Video",
            gallery: "Galeri",
            readPaper: "Makaleyi oku",
        },
        hero: {
            tagYear: "Araştırma Projesi",
            tagDomain: "Robotik · YZ · ARK",
            titleLine1: "Arama ve Kurtarma",
            titleLine2: "Robotu",
            tagline:
                "Felaket müdahalesinin kritik ilk saatlerinde otonom IR insan tespiti ve haritalama.",
            body: "RGB ve kızılötesi görüşü hibrit bir SLAM çerçevesiyle birleştiren, bilinmeyen ortamları haritalayan otonom bir robot, telaşlı aramayı hassas, hayat kurtaran bir kurtarmaya dönüştürür.",
            ctaMission: "Görevi gör",
            ctaMethodology: "Metodolojiye bak",
            ctaVideo: "Videoyu izle",
            missionTime: "Görev Süresi",
            missionStatus:
                "Bozulmuş görüş koşullarında hayatta kalan tespit edildi.",
            unitLabel: "ÜNİTE · D-07",
            statusLabel: "DURUM · AKTİF",
            feedLabel: "RGB + IR · FÜZYON",
            recLabel: "KYT",
            pillLabel: "Durum",
            pillBody: "Bozulmuş görüş koşullarında hayatta kalan tespit edildi.",
        },
        marquee: [
            "ArUcoSLAM",
            "Kızılötesi Görüş",
            "Derin Öğrenme Tespiti",
            "Odometri Füzyonu",
            "Anlamsal Haritalama",
            "GPS'siz Ortamlar",
            "Otonom Keşif",
        ],
        journey: {
            tag: "Görev · Hikâye",
            heading: "Enkazdan kurtarmaya, beş adımda.",
            narrative:
                "Depremin ardından arama-kurtarma ekipleri için en elzem bilgi, enkaz altındaki insanların tam konumudur. Biz de enkaz altındaki insanları tespit eden ve konumlarını doğrudan arama-kurtarma ekiplerine bildiren bir sistem tasarladık. Algoritmalardan oluşan bu sistemi önce simülasyonda, ardından gerçek bir robotta denedik. Enkaz altı karanlık bir ortamdır, bu yüzden robota IR kamera yerleştirdik, bir insan tespit modeli eğittik ve RGB + IR füzyonu yaptık. Sıradan RGB karanlıkta yetersiz kalırken bizim modelimiz tespiti sürdürebiliyor. Ayrıca GPS enkaz altında çalışmadığı için robotun kendi konumunu kendisi belirlemesini sağladık, odometri verisini ve ArUco markerlarını birlikte kullanıyoruz. Sonuçta robot, alanı haritalıyor, düşük ışıkta insanları tespit ediyor ve kendi konumunu gerçek zamanlı olarak sürekli güncelliyor.",
            steps: [
                {
                    title: "Enkazın altında",
                    body: "Depremin ardından kurtarma ekiplerinin önündeki darboğaz güç değil bilgidir: Hayatta kalanlar tam olarak nerede?",
                },
                {
                    title: "Arama değil, koordinat",
                    body: "Enkaz altındaki insanları tespit eden ve tam koordinatlarını kurtarma ekibine ileten bir algoritmik sistem tasarladık.",
                },
                {
                    title: "İki kez sınandı",
                    body: "Tüm sistemi önce simülasyonda, ardından laboratuvarımızdaki gerçek bir robotta yeniden doğruladık.",
                },
                {
                    title: "Karanlıkta görmek",
                    body: "Enkaz altı karanlıktır. Robota IR kamera yerleştirdik, insan tespit modelini eğittik ve RGB + IR füzyonu yaptık, sıradan RGB sönerken bizim modelimiz hayatta kalanı bulabiliyor.",
                },
                {
                    title: "GPS olmadan haritalama",
                    body: "Enkaz altında GPS çalışmaz. Tekerlek odometrisi ve ArUco markerlar sayesinde robot kendi konumunu çıkarıyor, alanı haritalıyor ve pozisyonunu gerçek zamanlı olarak güncelliyor.",
                },
            ],
        },
        intro: {
            tag: "01 · Giriş",
            heading: "Her şey ilk saatlerde başlar.",
            body: "Felaket müdahalesinde ilk saatler kritiktir. Arama-kurtarma robotumuz bilinmeyen ortamları haritalıyor ve hayatta kalanların koordinatlarını gerçek zamanlı sağlıyor, böylece ekipler arama aşamasını atlayıp doğrudan hayat kurtaran kurtarmaya geçebiliyor.",
            cards: [
                {
                    label: "01",
                    title: "Çok Modlu Algı",
                    body: "RGB ve kızılötesi görüş füzyonu, tam karanlıkta veya dumanlı odalarda bile hayatta kalanları yüksek güvenle tespit eder.",
                },
                {
                    label: "02",
                    title: "Hassas Konumlandırma",
                    body: "ArUcoSLAM ve tekerlek odometrisi, GPS'in tamamen başarısız olduğu karmaşık engellerde yüksek doğruluklu konumlandırma sağlar.",
                },
                {
                    label: "03",
                    title: "Taktiksel Anlamsal Haritalama",
                    body: "Gerçek zamanlı tespit ve derinlik kestirimi, hayatta kalanların koordinatlarını canlı bir 2B panoda eşsiz işaret olarak sabitler.",
                },
                {
                    label: "04",
                    title: "Operasyonel Verimlilik",
                    body: "Doğrulanmış hayatta kalan konumları manuel arama aşamasını ortadan kaldırır; ekipler doğrudan hayat kurtaran kurtarmaya geçer.",
                },
            ],
        },
        research: {
            tagQ: "02 · Araştırma Sorusu",
            question:
                "Hibrit bir konumlandırma sistemi, felaket ortamlarında hayatta kalan tespiti için haritalama doğruluğunu ne kadar etkin koruyabilir?",
            tagObj: "Araştırma Hedefleri",
            objectives: [
                "ArUcoSLAM ve tekerlek enkoder verisi kullanarak GPS'siz, görsel olarak bozulmuş felaket alanlarında güvenilir çalışan kararlı bir konumlandırma çerçevesi geliştirmek.",
                "Felaket koşullarında hayatta kalanları belirlemek için RGB ve kızılötesi (IR) verilerinin entegrasyonunun etkinliğini değerlendirmek.",
                "Hayatta kalanların konumlarını otomatik işaretleyen ve eyleme dönük koordinatları kurtarma ekipleriyle paylaşan dinamik bir uzamsal harita uygulamak.",
                "Felaket ortamının doğrulanmış haritasını sağlayarak \"arama-kurtarma\" geçiş süresini en aza indirmek.",
            ],
        },
        methodology: {
            tag: "03 · Metodoloji",
            heading: "Otonom bir görevin dört temeli.",
            sub: "Her modül bağımsız çalışacak şekilde tasarlandı ve tek bir tutarlı döngüde birleşiyor, algı, konumlandırma, haritalama, keşif.",
            cards: [
                {
                    title: "Hibrit Konumlandırma",
                    body: "ArUco markerlar ve tekerlek odometrisi ile sürekli, sapmasız otonom navigasyon.",
                },
                {
                    title: "Derin Öğrenme Tespiti",
                    body: "Düşük ışıklı, düşük kontrastlı felaket senaryoları için optimize edilmiş gerçek zamanlı insan tespit modeli.",
                },
                {
                    title: "Anlamsal Haritalama",
                    body: "Anlık koordinat hesabı, derinlik projeksiyonu ve hayatta kalanların canlı 2B panoda işaretlenmesi.",
                },
                {
                    title: "Otonom Keşif",
                    body: "Bilinmeyen ortamlarda kendi kararıyla yürütülen arama-kurtarma görev planlaması.",
                },
            ],
        },
        positioning: {
            tag: "04, Konum Güvenilirliği",
            heading: "Odayı asla kaybetmeyen, birleşik bir poz.",
            hybridTitle: "Hibrit Füzyon",
            hybridBody:
                "ArUco markerların tekerlek odometrisi ile birleştirilmesi, uzun otonom geçişlerde sapma sorununu ortadan kaldırdı.",
            stabilityTitle: "Kararlılık",
            stabilityBody:
                "Görsel markerlar geçici kapansa veya ışık yetersiz kalsa bile sistem tutarlı bir birleşik poz sürdürdü.",
            stats: [
                { v: "≈0 cm", l: "10 m'de Sapma" },
                { v: "RGB+IR", l: "Görüş Modu" },
                { v: "Canlı", l: "Haritalama" },
            ],
            imgTop: "RGB · IR · ÇİFT KANAL",
            imgBottom: "HAYATTA KALAN YERDE",
            imgAlt: "Test pistinde yan yana RGB ve IR görüş karşılaştırması",
        },
        chart: {
            tag: "Görev · İnfografik",
            heading: "Felaketten kurtarmaya giden yol.",
            sub: "Görevi baştan sona izleyen tek bir görsel, her durak otonom arama-kurtarma hattındaki bir aşamayı temsil ediyor.",
            figLabel: "Fig.02 · Görev Yolu",
            figRight: "ARK · 2025",
            imgAlt: "Görev infografiği, felaketten kurtarmaya kadar beş durak.",
            legend: [
                { n: "01", l: "Felaket meydana gelir" },
                { n: "02", l: "Konum belirlenir" },
                { n: "03", l: "Robot konuşlandırılır" },
                { n: "04", l: "Hayatta kalan tespit edilir" },
                { n: "05", l: "Kurtarma ve tahliye" },
            ],
        },
        analysis: {
            tag: "05 · Analiz ve Tartışma",
            heading: "RGB yetersiz kaldığında IR devreye girer.",
            col1Title: "Tespit Performansı",
            col1: [
                {
                    t: "RGB · IR Sinerjisi",
                    b: "RGB bozulmuş koşullarda zorlanırken IR entegrasyonu yüksek tespit güveni sağladı, algı boşluğu uçtan uca kapatıldı.",
                },
                {
                    t: "Sağlamlık",
                    b: "YZ modeli simüle edilmiş felaket koşullarında insan hedeflerini enkazdan başarıyla ayırt etti.",
                },
            ],
            col2Title: "Taktiksel İstihbarat",
            col2: [
                {
                    t: "Uzamsal Sabitleme",
                    b: "Kurban koordinatları gerçek zamanlı açı ve derinlik kestirimi ile 2B haritaya yansıtılıyor.",
                },
                {
                    t: "Eşsiz Yer İmi Sabitleme",
                    b: "Sistem tespit edilen konumları tekrarı önleyecek şekilde sabitliyor; ekipler için temiz ve eşsiz bir hayatta kalan listesi oluşturuyor.",
                },
            ],
        },
        results: {
            tag: "06 · Sonuç",
            body1: "Hayatta kalan konumunu otomatikleştirerek sistemimiz müdahale süresini en aza indiriyor ve kritik felaket senaryolarında keşif ile kurtarma arasındaki",
            highlight: "boşluğu kapatıyor",
            body2: ".",
            cta: "Galeriyi gör",
        },
        video: {
            tag: "Gösteri · 01",
            heading: "Robot iş başında.",
            sub: "Simüle bir felaket alanında otonom bir geçiş, tespit, haritalama ve raporlama.",
            duration: "Süre",
            modules: "Modüller",
            figLeft: "Fig.02 · Saha geçişi",
            figRight: "Saha gösterisi",
            abstractTag: "Özet",
            abstractTitle:
                "Sessiz bir odadan doğrulanmış bir kurtarma konumuna, beş dakikadan kısa sürede.",
            abstractP1:
                "Bu gösteri, robotu simüle edilmiş, GPS'siz bir felaket ortamında keşif yaparken yakalıyor. ArUco markerlar ve tekerlek odometrisi ile birleşik bir poz sürdürürken RGB + kızılötesi akışlar, düşük ışık ve enkazla gizlenmiş bir insan silüetini tespit etmek için birlikte çalışıyor.",
            abstractP2:
                "Hayatta kalan tespit edildiğinde sistem konumu canlı 2B haritaya yansıtır ve onu eşsiz bir yer imi olarak sabitler, kurtarma ekiplerine iletilebilecek eyleme dönük bir koordinat üretir.",
            ctaGallery: "Galeriyi aç",
            ctaHome: "Genel bakışa dön",
        },
        gallery: {
            tag: "Arşiv · Görsel kayıt",
            heading: "Proje, fotoğraflarla.",
            sub: "Donanım, saha senaryoları, IR yakalamaları ve canlı haritalama, robotun iş başındaki seçilmiş arşivi. Büyütmek için herhangi bir görsele tıklayın.",
            captions: [
                "YOLO çıktısı, düzensiz felaket alanında tespit edilen hayatta kalanlar (0.30 m ve 0.21 m okumaları)",
                "Canlı 2B SLAM haritası, robot (sarı), görüş konisi ve tespit edilen hayatta kalan H0",
                "Test pistinde canlı insan tespiti, 0.26 m (0.89) ve 0.49 m (0.82) iki kişi",
                "Sahadaki robot, çift kamera ve ölçek için 3B baskı hayatta kalan figürü",
                "RGB ve IR yan yana, test pistinde ayakta duran kişi ve yerdeki hayatta kalan",
                "Şasi, Raspberry Pi 4, Pi kamera, çift görüş donanımı ve sarı tekerlekler",
                "Simüle edilmiş iç mekan felaket ortamının üstten görünümü, birden fazla hayatta kalan",
                "Felaket simülasyonu, robotun yapının içine girdiği farklı bir an",
                "RGB ve IR çift hattı, aynı sahnenin iki algı kanalında işlenmiş hâli",
                "Elde tutulan ArUco markerı (id=0) ve canlı poz eksen kaplaması, tek markerla konum",
                "ArUco modu, laboratuvarda rqt image view ile gerçek zamanlı marker konumlandırma",
                "ArUcoSLAM gridinde (0–15) seçilen Marker ID:0, poz ve koordinatlar sabitlendi",
                "YOLO çıkarımı, test pistinde derinlik ve güven değerleriyle sınırlayıcı kutular",
                "duckie_processed ve pi_camera, RGB akışın yanında işlenmiş IR hattı çıktısı",
            ],
        },
        footer: {
            tagline:
                "Keşif ile kurtarma arasındaki boşluğu kapatıyoruz, bir otonom görev birden.",
            sections: "Bölümler",
            overview: "Proje genel bakış",
            video: "Gösteri videosu",
            gallery: "Görsel galerisi",
            focus: "Araştırma odağı",
            focusList: [
                "Otonom Arama ve Kurtarma",
                "Hibrit RGB + Kızılötesi Görüş",
                "ArUcoSLAM Konumlandırma",
                "Gerçek Zamanlı Anlamsal Haritalama",
            ],
            copyright:
                "Arama ve Kurtarma Robotu Araştırma Projesi. Tüm hakları saklıdır.",
            tagsBottom: "Otonom · IR · Tespit · Haritalama",
            supervisorLabel: "Danışman",
            supervisorName: "Dr. Öğr. Üyesi Özgür Erkent",
            membersLabel: "Üyeler",
            members: ["Sümeyra Koç", "Mert Kılınçer", "Ayşe Zülal Yıldız"],
            ackLabel: "Teşekkür",
            ackText: "Duckiebot'lar, Bridge to Turkey Fund tarafından NVIDIA sponsorluğunda finanse edilen \"Teaching SLAM with Autonomous Robots For Rescue Tasks\" projesi kapsamında sağlanmıştır.",
        },
    },
};

const Ctx = createContext({
    lang: "en",
    t: (k) => k,
    pick: (en) => en,
    toggle: () => {},
});

export const LanguageProvider = ({ children }) => {
    const [lang, setLang] = useState(() => {
        if (typeof window === "undefined") return "en";
        return localStorage.getItem("sar_lang") || "en";
    });

    useEffect(() => {
        try {
            localStorage.setItem("sar_lang", lang);
        } catch (e) {
            // ignore
        }
        if (typeof document !== "undefined") {
            document.documentElement.lang = lang;
        }
    }, [lang]);

    const t = (path) => {
        const parts = path.split(".");
        let cur = translations[lang];
        for (const p of parts) {
            if (cur == null) return path;
            cur = cur[p];
        }
        return cur ?? path;
    };

    const pick = (en, tr) => (lang === "tr" ? tr : en);

    const toggle = () => setLang((l) => (l === "en" ? "tr" : "en"));

    return (
        <Ctx.Provider value={{ lang, t, pick, toggle, setLang }}>
            {children}
        </Ctx.Provider>
    );
};

export const useT = () => useContext(Ctx);
